"use client";

import { Brain, Database, Check } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { cognitiveBiasIntegration as data } from "@/lib/data/capabilities-narrative-engineering";

const domainAccents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
  "var(--color-amber)",
] as const;

const mappingAccents = [
  "var(--color-violet)",
  "var(--color-denim)",
  "var(--color-emerald)",
] as const;

export function CognitiveBiasIntegrationSection() {
  return (
    <Section id="cognitive-bias-integration" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/3 h-80 w-80 rounded-full bg-[var(--color-pink)] opacity-10 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-rose)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Database className="h-4 w-4 text-[var(--color-purple)]" />
              <div className="eyebrow-tiny text-[var(--color-purple)]">The 200+ Bias Library</div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.domains.map((d, i) => {
                const accent = domainAccents[i % domainAccents.length]!;
                return (
                  <div
                    key={d.name}
                    className="glass rounded-2xl p-5 h-full"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 14%, transparent)`,
                    }}
                  >
                    <div
                      className="eyebrow-tiny font-bold mb-3"
                      style={{ color: accent }}
                    >
                      Domain {i + 1}
                    </div>
                    <h3 className="text-sm font-bold">{d.name}</h3>
                    <ul className="mt-3 space-y-1.5">
                      {d.biases.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">
                          <Check className="h-3 w-3 mt-0.5 shrink-0" style={{ color: accent }} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={220}>
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="h-4 w-4 text-[var(--color-violet)]" />
              <div className="eyebrow-tiny text-[var(--color-violet)]">Bias-Narrative Mapping</div>
            </div>
            <div className="space-y-4">
              {data.mapping.map((m, i) => {
                const accent = mappingAccents[i % mappingAccents.length]!;
                return (
                  <div
                    key={m.level}
                    className="glass rounded-2xl p-6"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 14%, transparent)`,
                    }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="grid place-items-center h-8 w-8 rounded-lg text-[11px] font-bold"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          color: accent,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="text-sm font-bold">{m.level}</h3>
                    </div>
                    <p className="text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{m.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
